import { useState, useRef } from 'react'
import clsx from 'clsx'
import BigNumber from 'bignumber.js'
import PercentageSlider from '@/components/PercentageSlider'
import IconToken from '@/components/icons/IconToken'
import IconChevronDown from '@/components/icons/IconChevronDown'
import IconColorClose from '@/components/icons/IconColorClose'
import useOnClickOutside from '@/hooks/useOnClickOutside'
import numberFormat, { isValidNumber } from '@/utils/numberFormat'
import './AssetInputBlock.css'

export default function AssetInputBlock({
  token,
  balance,
  amount = '',
  decimalPlaces,
  assetList,
  onClose,
  onUpdateToken,
  onUpdate
}) {
  const inputRef = useRef()
  const assetSelectorRef = useRef()
  const [focus, setFocus] = useState(false)
  const [assetSelectorActive, setAssetSelectorActive] = useState(false)
  useOnClickOutside(assetSelectorRef, () => setAssetSelectorActive(false))
  const initialPercentage = BigNumber(amount || 0)
    .div(balance)
    .times(100)
  const [percentage, setPercentage] = useState(
    initialPercentage.isFinite()
      ? Math.min(initialPercentage.dp(0, BigNumber.ROUND_DOWN).toNumber(), 100)
      : 0
  )
  // const maxDecimalPlaces = BigNumber(balance).dp()
  const focusInput = () => inputRef.current.focus()
  const onAmountChange = e => {
    const value = e.target.value
    if (!isValidNumber(value) || BigNumber(value || 0).dp() > decimalPlaces) {
      return
    }
    const amountPercentage = BigNumber(balance).eq(0)
      ? 0
      : Math.min(
          BigNumber(value || 0)
            .div(balance)
            .times(100)
            .dp(0, BigNumber.ROUND_DOWN)
            .toNumber(),
          100
        )
    setPercentage(amountPercentage)
    onUpdate(token, value)
  }
  const onPercentageChange = percentage => {
    setPercentage(percentage)
    onUpdate(
      token,
      BigNumber(balance)
        .times(percentage)
        .div(100)
        .dp(decimalPlaces, BigNumber.ROUND_DOWN)
        .toNumber()
    )
  }

  const isError = BigNumber(amount || 0).gt(balance)
  const error = isError ? 'Insufficient balance' : ''
  const assetSelectorAvailable = assetList.length > 0
  const AssetList = assetList.map(symbol => {
    return (
      <div
        key={symbol}
        className="asset-list-row"
        onClick={() => onUpdateToken(symbol)}
      >
        <IconToken className="asset-list-row-icon" symbol={symbol} />
        {symbol}
      </div>
    )
  })

  return (
    <div className="asset-input-block noselect">
      <div className="balance-label">
        Available
        <span className="balance" onClick={() => onPercentageChange(100)}>
          {numberFormat(balance, 'max8')}
        </span>
        {token}
      </div>
      <div
        className={clsx('asset-input-container', { focus, error })}
        onClick={focusInput}
      >
        <div className="asset-input-label">Amount</div>
        <input
          ref={inputRef}
          className="asset-input"
          type="text"
          value={amount}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          onChange={onAmountChange}
        />
        <div className="vertical-divider" />
        <div
          ref={assetSelectorRef}
          className={clsx('asset-selector', {
            active: assetSelectorActive
          })}
          onClick={() => setAssetSelectorActive(active => !active)}
        >
          <IconToken symbol={token} />
          <div className="asset-name">{token}</div>
          {assetSelectorAvailable && (
            <>
              <IconChevronDown className="icon-chevron-down" />
              <div className="asset-list">{AssetList}</div>
            </>
          )}
        </div>
        {error && <div className="error-message">{error}</div>}
      </div>
      <PercentageSlider
        value={percentage}
        showValueLabelOnLeft={true}
        onUpdate={onPercentageChange}
      />
      {onClose && (
        <IconColorClose className="remove-asset-button" onClick={onClose} />
      )}
    </div>
  )
}
